import { useEffect, useState } from "react";
import {
  getAdminAudit,
  getAdminUsers,
  getOperatorEvents,
  AdminAuditRow,
  AdminAuditParams,
  AdminUserRow,
  OperatorEventRow,
} from "../../lib/adminApi";
import { labelForEvent, formatUsd, EVENT_LABELS, shortId } from "../../lib/adminLabels";
import { AdminNav } from "./AdminNav";
import { AdminUserFilter } from "./AdminUserFilter";
import { Card } from "../../ui/Card";
import { Skeleton } from "../../ui/Skeleton";
import { EmptyState } from "../../ui/EmptyState";

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** First 8 chars of a UUID meeting_id, or "—" for null. */
function shortMeetingId(id: string | null): string {
  if (!id) return "—";
  return shortId(id);
}

const OPERATOR_ACTION_LABELS: Record<string, string> = {
  user_invited: "Invited user",
  invite_revoked: "Revoked invite",
  user_blocked: "Blocked user",
  user_unblocked: "Unblocked user",
  user_deleted: "Deleted user",
};

function labelForOperatorAction(action: string): string {
  return OPERATOR_ACTION_LABELS[action] ?? action;
}

// Build a sorted list of distinct event type options for the filter <select>.
// We expose all known labels plus deduplicate against the live rows.
function buildEventOptions(rows: AdminAuditRow[]): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  const options: Array<{ value: string; label: string }> = [];

  // Add types from known vocabulary first (alphabetical by label).
  const knownSorted = Object.entries(EVENT_LABELS).sort((a, b) =>
    a[1].localeCompare(b[1]),
  );
  for (const [value, label] of knownSorted) {
    if (!seen.has(value)) {
      seen.add(value);
      options.push({ value, label });
    }
  }

  // Add any types from live data not in the vocabulary (pass-through labels).
  for (const row of rows) {
    if (!seen.has(row.event_type)) {
      seen.add(row.event_type);
      options.push({ value: row.event_type, label: row.event_type });
    }
  }

  return options;
}

export function AdminAudit() {
  const [rows, setRows] = useState<AdminAuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [filterEventType, setFilterEventType] = useState("");
  const [filterUserId, setFilterUserId] = useState("");

  // Users for the type-to-search filter (loaded once; filtered locally).
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    getAdminUsers()
      .then((u) => {
        if (!cancelled) setUsers(u);
      })
      .catch(() => {
        // Non-fatal: the audit still works; the filter just shows no matches.
        if (!cancelled) setUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Operator events (platform-operator actions: invite/revoke/block/unblock/delete).
  const [operatorEvents, setOperatorEvents] = useState<OperatorEventRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    getOperatorEvents()
      .then((evts) => {
        if (!cancelled) setOperatorEvents(evts);
      })
      .catch(() => {
        if (!cancelled) setOperatorEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Single effect keyed on filters — also runs on mount (covers initial load).
  // Previously two effects (one on [], one on [filterEventType, filterUserId])
  // both fired on mount, causing two racing requests.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params: AdminAuditParams = {};
    if (filterEventType) params.event_type = filterEventType;
    if (filterUserId.trim()) params.user_id = filterUserId.trim();
    getAdminAudit(params)
      .then((d) => {
        if (!cancelled) {
          setRows(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  // Re-fetch whenever filters change (also fires on initial mount).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterEventType, filterUserId]);

  const eventOptions = buildEventOptions(rows);

  return (
    <>
      <AdminNav />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Audit log</h2>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200 }}>
          <label htmlFor="audit-event-filter" style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 600 }}>
            Filter by action
          </label>
          <select
            id="audit-event-filter"
            value={filterEventType}
            onChange={(e) => setFilterEventType(e.target.value)}
            className="select-kit"
            style={{ width: "auto" }}
          >
            <option value="">All actions</option>
            {eventOptions.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 240 }}>
          <span style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 600 }}>
            Filter by user
          </span>
          <AdminUserFilter
            users={users}
            value={filterUserId}
            onChange={setFilterUserId}
          />
        </div>
      </div>

      {error && <p className="error" style={{ marginBottom: 12 }}>{error}</p>}

      {loading ? (
        <Card style={{ padding: 16 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} style={{ display: "flex", gap: 16, alignItems: "center", paddingBottom: 12 }}>
              <Skeleton height={12} width="16%" />
              <Skeleton height={12} width="22%" />
              <Skeleton height={12} width="18%" />
              <Skeleton height={12} width="10%" />
              <Skeleton height={12} width="8%" />
            </div>
          ))}
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState icon="file-text" title="No events yet" description="Actions will appear here as users work." />
      ) : (
        <Card style={{ padding: 0, overflowX: "auto" }}>
          <table className="meetings">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Who</th>
                <th>Transcript</th>
                <th style={{ textAlign: "end" }}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td style={{ fontSize: 12, color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                    {formatWhen(row.created_at)}
                  </td>
                  <td style={{ fontSize: 13 }}>{labelForEvent(row.event_type)}</td>
                  <td style={{ fontSize: 13 }}>
                    {row.actor_email ?? <span className="muted">system</span>}
                  </td>
                  <td style={{ fontSize: 12, fontFamily: "monospace", color: "var(--ink-3)" }}>
                    {shortMeetingId(row.meeting_id)}
                  </td>
                  <td style={{ fontSize: 13, textAlign: "end" }}>
                    {row.cost_usd != null ? formatUsd(row.cost_usd) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Operator actions section */}
      {operatorEvents.length > 0 && (
        <>
          <h3 style={{ marginTop: 32, marginBottom: 12 }}>Operator actions</h3>
          <Card style={{ padding: 0, overflowX: "auto" }}>
            <table className="meetings">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>By</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {operatorEvents.map((evt) => (
                  <tr key={evt.id}>
                    <td style={{ fontSize: 12, color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                      {formatWhen(evt.created_at)}
                    </td>
                    <td style={{ fontSize: 13 }}>{labelForOperatorAction(evt.action)}</td>
                    <td style={{ fontSize: 13 }}>
                      {evt.actor_email ?? <span className="muted">—</span>}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {evt.target_email ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </>
  );
}
